import { Accordion, AccordionSummary, Container, Divider, Grid, Link } from '@material-ui/core';
import { AccountOutlinedIcon } from 'components/icons/account-outlined';
import { CalendarOutlinedIcon } from 'components/icons/calendar-outlined';
import { ChevronDownOutlinedIcon } from 'components/icons/chevron-down-outlined';
import { LocationOutlinedIcon } from 'components/icons/location-outlined-icon';
import { SaleOutlinedIcon } from 'components/icons/sale-outlined';
import React, { useEffect, useRef, useState } from 'react';
import { useBreakpoints } from 'utils/use-breakpoints';
import { Catalogues } from '../catalogue-constants.enum';
import { changeDateFormat, displaySaleType, formatNumber } from '../catalogue-utils';
import { CatalogueBannerProps } from './catalogue-banner-props';
import './catalogue-banner.scss';

/**
 * A catalogue banner.
 * @param props
 */
export const CatalogueBanner: React.FC<CatalogueBannerProps> = (props) => {
  const { catalogue } = props;
  const { phone, tablet } = useBreakpoints();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [enableReadMore, setEnableReadMore] = useState<boolean>(false);
  const [readMore, setReadMore] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // sets auto width of containers and columns if banner is for semen catalogue
  const catalougeTabletSize = props.catalogueType === Catalogues.IndividualSemen ? 'auto' : 12;

  /**
   * Sets sticky banner
   */
  useEffect(() => {
    const banner = bannerRef.current;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSticky(!entry.isIntersecting);
    }, { threshold: 0 });
    observer.observe(banner as HTMLElement);

    return () => {
      observer.unobserve(banner as HTMLElement);
    };
  }, []);

  /**
   * Enables the 'read more' button if the description scroll height is
   * larger than the actual height.
   */
  useEffect(() => {
    const desc = descriptionRef.current;
    if (desc) {
      const enable = desc.scrollHeight > desc.clientHeight;
      setEnableReadMore(enable);
    }
  }, []);

  /**
   * Adds or removes a max height to the description div based on whether
   * 'show more' has been selected or not to allow a expand/collapse animation.
   *
   * - true: Sets the max height to the scrollheight of the div.
   * - false: Removes the max height.
   */
  useEffect(() => {
    const desc = descriptionRef.current;
    if (desc) {
      if (readMore) {
        const height = desc.scrollHeight;

        desc.setAttribute('style', `max-height: ${height}px`);
      } else {
        desc.removeAttribute('style');
      }
    }
  }, [readMore]);

  return (
    <div className="sg-catalogue-banner">
      <div id="sg-catalogue-header" className={`sg-catalogue-banner__sticky ${isSticky ? 'sg-catalogue-banner__sticky--visible' : ''}`}>
        <Container>
          <Grid>
            <h4>{catalogue.title}</h4>
          </Grid>
        </Container>
      </div>
      <Container ref={bannerRef}>
        <Grid container>
          <Grid item sm={catalougeTabletSize} md={catalogue.catalogueImages.length ? 3 : 'auto'} xs={12}>
            <div className="sg-catalogue-banner__image-wrapper">
              {!!catalogue.catalogueImages.length
                && (
                  <div className={`sg-catalogue-banner__image ${props.catalogueType === Catalogues.IndividualSale ? 'sg-catalogue-banner__image--extra-space' : ''}`}>
                    <img alt="catalogue" src={`data:image/jpeg;base64,${catalogue.catalogueImages[0].image.imageBytes}`} />
                  </div>
                )}
              {(tablet && props.catalogueType === Catalogues.IndividualSale) && <h2 className="sg-catalogue-banner__title">{catalogue.title}</h2>}
            </div>
          </Grid>
          <Grid item sm={catalougeTabletSize} md={9} className="sg-catalogue-banner__info" xs={12}>
            {(!tablet || props.catalogueType === Catalogues.IndividualSemen) && <h2 className="sg-catalogue-banner__title">{catalogue.title}</h2>}
            <Grid container>
              {props.catalogueType === Catalogues.IndividualSale && (
                <Grid item sm={4} xs={12}>
                  <div className="sg-catalogue-banner__container">
                    <CalendarOutlinedIcon />
                    <div>
                      <p className="sg-catalogue-banner__date" dangerouslySetInnerHTML={{ __html: changeDateFormat(catalogue.saleStartDateDesc, catalogue.saleEndDateDesc) }} />
                      {!phone && (<br />)}
                    </div>
                  </div>
                  <div className="sg-catalogue-banner__container">
                    <SaleOutlinedIcon />
                    <div>
                      <p><b>{displaySaleType(catalogue.saleTypeDesc)}</b></p>
                      {catalogue.auctionUrl
                        && (
                          <Link
                            className="lower"
                            href={catalogue.auctionUrl}
                            target="_blank"
                            rel="noopener"
                          >
                            Auction website
                          </Link>
                        )}
                    </div>
                  </div>
                </Grid>
              )}
              <Grid item sm={catalougeTabletSize === 12 ? 4 : catalougeTabletSize} xs={12} className="sg-catalogue-banner__container">
                <AccountOutlinedIcon />
                <div>
                  <p><b>{catalogue.contactName}</b></p>
                  {
                    formatNumber(catalogue.contactPhoneNumberDesc).map((phone) => (<p key={phone}>{phone}</p>))
                  }
                  {catalogue.contactEmailDesc.length > 1
                    && (
                      <>
                        <Link
                          className="lower"
                          href={`mailto:${catalogue.contactEmailDesc}`}
                          target="_blank"
                          rel="noopener"
                        >
                          Email
                        </Link><br />
                      </>
                    )}
                  {catalogue.contactUrlDesc.length > 1
                    && (
                      <Link
                        className="lower"
                        href={catalogue.contactUrlDesc}
                        target="_blank"
                        rel="noopener"
                      >
                        Website
                      </Link>
                    )}
                </div>
              </Grid>
              {props.catalogueType === Catalogues.IndividualSale && (
                <Grid item sm={4} xs={12} className="sg-catalogue-banner__container">
                  <LocationOutlinedIcon />
                  <div className="sg-catalogue-banner__address">
                    <p><b>{catalogue.propertyNameDesc}</b></p>
                    <p>{catalogue.address}</p>
                    <p>{catalogue.city}, {catalogue.postcode}</p>
                    <p>{catalogue.state}</p>
                  </div>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {catalogue.description
          && (
            <Grid container className="sg-catalogue-banner__more">
              <Grid item xs={12}>
                <Divider variant="fullWidth" />
                <Grid item sm={8} className="sg-catalogue-banner__description">
                  <div
                    className="description"
                    ref={descriptionRef}
                  >
                    {catalogue.description}
                  </div>
                  {enableReadMore && (
                    <Accordion
                      className="read-more"
                      elevation={0}
                      expanded={readMore}
                      square
                      onChange={(event: any, e: boolean) => setReadMore(e)}
                    >
                      <AccordionSummary className="read-more__summary" expandIcon={<ChevronDownOutlinedIcon />}>
                        <span className="summary__title">Read {readMore ? 'less' : 'more'}</span>
                      </AccordionSummary>
                    </Accordion>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
      </Container>
    </div>
  );
};
